import React from "react";
import { useTheme } from "../../../context/ThemeContext";
import { Trash2, Check, Plus } from "lucide-react";

// 单行分镜 — 表格中的一行
// 列顺序：镜号 | 景别 | 生图提示词(重点,居中大列) | 画面描述 | 对白 | 操作
const ShotCardText = React.memo(({
  scene,
  index,
  handleChange,
  handleInsertRow,
  handleDeleteRow,
  selectedShots,
  toggleShotSelection,
  isMultiSelectMode,
  isGlobalLocked,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const isSelected = selectedShots?.has(index);

  const cellBorder = '1px solid var(--color-border-secondary)';

  return (
    <tr
      className="group transition-all duration-200"
      style={{
        backgroundColor: isSelected ? `${theme.colors.accentPrimary}08` : 'transparent',
      }}
      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; }}
      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {/* 镜号 */}
      <td className="px-3 py-2 text-center align-top" style={{ borderBottom: cellBorder, borderRight: cellBorder }}>
        <div className="flex items-center gap-1.5 justify-center">
          {isMultiSelectMode && (
            <button
              onClick={() => toggleShotSelection(index)}
              className="w-5 h-5 rounded flex items-center justify-center transition-all duration-200 flex-shrink-0"
              style={{
                backgroundColor: isSelected ? theme.colors.accentPrimary : 'transparent',
                border: `1.5px solid ${isSelected ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`,
                color: isSelected ? 'white' : 'transparent',
              }}
            >
              <Check size={10} strokeWidth={3} />
            </button>
          )}
          <span className="text-xs font-bold font-mono" style={{ color: theme.colors.accentPrimary }}>
            {scene.shot_number}
          </span>
        </div>
      </td>

      {/* 景别 */}
      <td className="px-2 py-2 align-top" style={{ borderBottom: cellBorder, borderRight: cellBorder }}>
        <input
          value={scene.shot || ''}
          onChange={(e) => handleChange(index, "shot", e.target.value)}
          className="w-full bg-transparent text-xs outline-none px-1 py-0.5 rounded"
          style={{ color: 'var(--color-text-primary)' }}
          placeholder="景别"
        />
      </td>

      {/* ★ 生图提示词 — 核心列，最宽，居中 */}
      <td className="px-3 py-2 align-top" style={{ borderBottom: cellBorder, borderRight: cellBorder, backgroundColor: isSelected ? undefined : `${theme.colors.accentPrimary}04` }}>
        <textarea
          value={scene.image_prompt || ''}
          onChange={(e) => handleChange(index, "image_prompt", e.target.value)}
          className="w-full rounded px-2 py-1.5 text-xs font-mono outline-none resize-none leading-relaxed overflow-hidden"
          style={{
            backgroundColor: 'transparent',
            color: 'var(--color-text-primary)',
            border: 'none',
          }}
          rows={3}
          placeholder="输入生图提示词..."
          onFocus={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)'; e.currentTarget.style.boxShadow = `inset 0 0 0 1px ${theme.colors.accentPrimary}50`; }}
          onBlur={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
        />
      </td>

      {/* 画面描述 */}
      <td className="px-2 py-2 align-top" style={{ borderBottom: cellBorder, borderRight: cellBorder }}>
        <textarea
          value={scene.visual_content || ''}
          onChange={(e) => handleChange(index, "visual_content", e.target.value)}
          className="w-full rounded px-2 py-1.5 text-xs outline-none resize-none leading-relaxed overflow-hidden"
          style={{ backgroundColor: 'transparent', color: 'var(--color-text-secondary)', border: 'none' }}
          rows={3}
          placeholder="画面描述..."
          onFocus={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)'; }}
          onBlur={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        />
      </td>

      {/* 对白 */}
      <td className="px-2 py-2 align-top" style={{ borderBottom: cellBorder, borderRight: cellBorder }}>
        <textarea
          value={scene.content || ''}
          onChange={(e) => handleChange(index, "content", e.target.value)}
          className="w-full rounded px-2 py-1.5 text-xs outline-none resize-none leading-relaxed overflow-hidden"
          style={{ backgroundColor: 'transparent', color: 'var(--color-text-secondary)', border: 'none' }}
          rows={3}
          placeholder="对白..."
          onFocus={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)'; }}
          onBlur={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        />
      </td>

      {/* 时长 */}
      <td className="px-2 py-2 align-top" style={{ borderBottom: cellBorder, borderRight: cellBorder }}>
        <input
          value={scene.time || ''}
          onChange={(e) => handleChange(index, "time", e.target.value)}
          className="w-full bg-transparent text-xs outline-none px-1 py-0.5 rounded text-center"
          style={{ color: 'var(--color-text-primary)' }}
          placeholder="时长"
        />
      </td>

      {/* 操作 */}
      <td className="px-2 py-2 text-center align-top" style={{ borderBottom: cellBorder }}>
        <div className="flex flex-col items-center gap-1">
          <button
            onClick={() => handleDeleteRow(index)}
            className="p-1 rounded opacity-0 group-hover:opacity-100 transition-all duration-200"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-error)'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
            title="删除"
          >
            <Trash2 size={12} />
          </button>
          <button
            onClick={() => handleInsertRow(index + 1)}
            disabled={isGlobalLocked}
            className="p-1 rounded opacity-0 group-hover:opacity-100 transition-all duration-200 disabled:opacity-0"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = theme.colors.accentPrimary; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
            title="在下方插入"
          >
            <Plus size={12} />
          </button>
        </div>
      </td>
    </tr>
  );
});

export default ShotCardText;
